import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CalendarCheck, CheckCircle2, ChevronLeft, ChevronRight, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TZ_CHOICES = [
  "America/Costa_Rica", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Mexico_City", "America/Bogota", "America/Sao_Paulo",
  "Europe/Madrid", "Europe/London", "Europe/Berlin", "Asia/Dubai", "Asia/Tokyo",
  "Australia/Sydney", "UTC",
];
const COPY = {
  es: {
    week: ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"],
    months: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
      "agosto", "septiembre", "octubre", "noviembre", "diciembre"],
    locale: "es-CR",
    pickType: "¿De qué querés que hablemos?", minutes: "minutos",
    tzNote: "Los horarios se muestran en tu hora local",
    noSlots: "Por ahora no hay espacios disponibles. Escribinos a",
    otherDay: "Elegir otro día", otherHour: "Cambiar la hora",
    hoursIn: "minutos · horas en tu zona",
    yourBooking: "Tu cita", name: "Nombre completo *", email: "Correo *", whatsapp: "WhatsApp *",
    confirm: "Confirmar reserva",
    smallPrint: "Te llega la confirmación por correo con el enlace, y recordatorios un día antes y una hora antes.",
    missing: "Completá tu nombre, correo y WhatsApp", missingQ: "Falta responder:",
    doneTitle: "¡Listo, quedó reservado!",
    doneNote: "Te enviamos la confirmación a %s con el enlace de la reunión. También te recordamos un día antes y una hora antes.",
    changeIt: "¿Necesitás cambiarla?", manage: "Reprogramar o cancelar",
    notFound: "Esta página de reservas no está disponible.",
    cycleNote: "Usá el mismo correo con el que estás registrado en la escuela: la clase se agenda dentro de tu ciclo y podés cancelarla o moverla hasta 12 horas antes.",
  },
  en: {
    week: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    months: ["January", "February", "March", "April", "May", "June", "July",
      "August", "September", "October", "November", "December"],
    locale: "en-US",
    pickType: "What would you like to talk about?", minutes: "minutes",
    tzNote: "Times are shown in your local time",
    noSlots: "There are no times available right now. Write to us at",
    otherDay: "Pick another day", otherHour: "Change the time",
    hoursIn: "minutes · times in your zone",
    yourBooking: "Your appointment", name: "Full name *", email: "Email *", whatsapp: "WhatsApp *",
    confirm: "Confirm booking",
    smallPrint: "You will get an email with the meeting link, plus reminders one day and one hour before.",
    missing: "Please fill in your name, email and WhatsApp", missingQ: "Please answer:",
    doneTitle: "You are booked!",
    doneNote: "We sent the confirmation to %s with the meeting link. We will also remind you one day and one hour before.",
    changeIt: "Need to change it?", manage: "Reschedule or cancel",
    notFound: "This booking page is not available.",
    cycleNote: "Use the same email you are registered with at the school: the class is booked inside your cycle and you can cancel or move it up to 12 hours before.",
  },
};

const guessLang = () => {
  const q = new URLSearchParams(window.location.search).get("lang");
  if (q) return q.toLowerCase().startsWith("en") ? "en" : "es";
  return (navigator.language || "es").toLowerCase().startsWith("en") ? "en" : "es";
};

const guessTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Costa_Rica"; }
  catch { return "America/Costa_Rica"; }
};

const FIELD =
  "w-full min-h-[48px] rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-[16px] text-slate-900 " +
  "placeholder:text-slate-400 focus:bg-white focus:border-[#004aad] focus:ring-2 focus:ring-[#004aad]/25 " +
  "focus:outline-none transition-colors duration-200";

/** Public booking page: month calendar → hours of that day → form. Mobile first. */
export default function BookingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [typeId, setTypeId] = useState("");
  const [days, setDays] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [tz, setTz] = useState(guessTz());
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [pickedDay, setPickedDay] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [lang, setLang] = useState(guessLang);
  const c = COPY[lang];
  const WEEK = c.week;
  const MONTHS = c.months;

  useEffect(() => {
    api.get(`/public/booking/${slug}`)
      .then((r) => { setPage(r.data); setTypeId(r.data.meeting_types?.[0]?.id || ""); })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!typeId) return;
    setLoadingSlots(true);
    setPickedDay(null);
    setChosen(null);
    api.get(`/public/booking/${slug}/slots`, { params: { type_id: typeId } })
      .then((r) => setDays(r.data.days || []))
      .catch(() => setDays([]))
      .finally(() => setLoadingSlots(false));
  }, [slug, typeId]);

  const tzOptions = useMemo(() => Array.from(new Set([guessTz(), ...TZ_CHOICES])), []);
  const byDate = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d.slots])), [days]);

  // Slots are grouped by the host's day; regroup them by the visitor's own date.
  const localDays = useMemo(() => {
    const map = {};
    for (const d of days) {
      for (const s of d.slots) {
        const key = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(s));
        (map[key] = map[key] || []).push(s);
      }
    }
    return map;
  }, [days, tz]);

  const hour = (iso) => new Date(iso).toLocaleTimeString(c.locale, { hour: "2-digit", minute: "2-digit", timeZone: tz });
  const longWhen = (iso) => new Date(iso).toLocaleString(c.locale, { dateStyle: "full", timeStyle: "short", timeZone: tz });

  const grid = useMemo(() => {
    const first = new Date(month.y, month.m, 1);
    const offset = (first.getDay() + 6) % 7;                 // week starts on Monday
    const total = new Date(month.y, month.m + 1, 0).getDate();
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= total; d++) {
      const key = `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, key, count: (localDays[key] || []).length });
    }
    return cells;
  }, [month, localDays]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(c.missing);
      return;
    }
    const pending = (page.questions || []).find((q) => {
      const a = answers[q.id];
      return Array.isArray(a) ? a.length === 0 : !String(a || "").trim();
    });
    if (pending) {
      toast.error(`${c.missingQ} ${pending.label}`);
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/public/booking/${slug}/book`, {
        type_id: typeId, start: chosen, timezone: tz, answers, lang,
        name: form.name, email: form.email, phone: form.phone,
      });
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reservar");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <p className="text-slate-600 text-sm" data-testid="booking-not-found">{COPY[guessLang()].notFound}</p>
    </div>;
  }
  if (!page) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <Loader2 className="h-6 w-6 animate-spin text-[#004aad]" />
    </div>;
  }

  const selectedType = (page.meeting_types || []).find((m) => m.id === typeId);
  const dayHours = pickedDay ? (localDays[pickedDay] || []) : [];

  const shell = (children) => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-14">
        <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5" data-testid="booking-lang-switch">
            {["es", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`booking-lang-${l}`}
                      className={`min-h-[36px] rounded-full px-3.5 text-xs font-semibold uppercase transition-colors ${
                        lang === l ? "bg-[#004aad] text-white" : "text-slate-500 hover:text-[#004aad]"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (done) {
    return shell(
      <div className="bg-white rounded-3xl border border-emerald-100 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-7 sm:p-11 text-center"
           data-testid="booking-success">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-5" />
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{c.doneTitle}</h1>
        <p className="text-base text-slate-600 mt-4 leading-relaxed">
          {done.type_name}<br />
          <span className="text-slate-900 font-medium">{longWhen(done.start)}</span>
          <span className="block text-sm text-slate-500 mt-1">{tz.replace("_", " ")}</span>
        </p>
        <p className="text-sm text-slate-500 mt-4" data-testid="booking-success-email-note">
          {c.doneNote.replace("%s", form.email)}
        </p>
        {done.manage_token && (
          <p className="text-sm text-slate-500 mt-6">
            {c.changeIt} <a className="text-[#004aad] font-medium hover:underline"
              href={`/reservar/gestion/${done.manage_token}?lang=${lang}`}>{c.manage}</a>
          </p>
        )}
      </div>
    );
  }

  const tzBar = (
    <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-100 text-sm text-slate-500">
      <Globe className="h-4 w-4 text-[#ff731d] shrink-0" />
      <select value={tz} onChange={(e) => { setTz(e.target.value); setPickedDay(null); setChosen(null); }}
              className="min-h-[40px] flex-1 bg-transparent text-slate-700 focus:outline-none"
              data-testid="booking-tz-select">
        {tzOptions.map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
      </select>
    </div>
  );

  // ---------- step 3: form ----------
  if (chosen) {
    return shell(
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-5 sm:p-8"
           data-testid="booking-form">
        <button onClick={() => setChosen(null)} data-testid="booking-back-to-hours"
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004aad] mb-5">
          <ArrowLeft className="h-4 w-4" /> {c.otherHour}
        </button>
        <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
          <div className="flex items-start gap-2.5">
            <CalendarCheck className="h-5 w-5 text-[#ff731d] mt-0.5 shrink-0" />
            <div>
              <p className="text-xs tracking-[0.1em] uppercase font-semibold text-slate-500">{c.yourBooking}</p>
              <p className="text-base font-semibold text-slate-900">{longWhen(chosen)}</p>
              <p className="text-sm text-slate-500 mt-0.5">
                {selectedType?.name} · {selectedType?.minutes} min · {tz.replace("_", " ")}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 mt-6">
          <div>
            <label className="text-xs tracking-[0.1em] uppercase font-semibold text-slate-500">{c.name}</label>
            <input className={`${FIELD} mt-2`} value={form.name} data-testid="booking-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs tracking-[0.1em] uppercase font-semibold text-slate-500">{c.email}</label>
            <input className={`${FIELD} mt-2`} type="email" inputMode="email" value={form.email}
                   data-testid="booking-email-input" onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="text-xs tracking-[0.1em] uppercase font-semibold text-slate-500">{c.whatsapp}</label>
            <input className={`${FIELD} mt-2`} inputMode="tel" value={form.phone} data-testid="booking-phone-input"
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>

          {(page.questions || []).map((q) => (
            <div key={q.id} data-testid={`booking-question-${q.id}`}>
              <label className="text-xs tracking-[0.1em] uppercase font-semibold text-slate-500">
                {q.label} *
              </label>
              {q.type === "text" && (
                <input className={`${FIELD} mt-2`} value={answers[q.id] || ""}
                       data-testid={`booking-answer-${q.id}`}
                       onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
              )}
              {q.type === "choice" && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.options.map((o) => (
                    <button key={o} type="button" onClick={() => setAnswers({ ...answers, [q.id]: o })}
                            data-testid={`booking-answer-${q.id}-${o}`}
                            className={`min-h-[44px] rounded-full px-4 text-sm font-medium border transition-colors ${
                              answers[q.id] === o
                                ? "bg-[#004aad] text-white border-[#004aad]"
                                : "bg-white text-slate-700 border-slate-200 hover:border-[#004aad]"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              )}
              {q.type === "multi" && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.options.map((o) => {
                    const list = answers[q.id] || [];
                    const on = list.includes(o);
                    return (
                      <button key={o} type="button" data-testid={`booking-answer-${q.id}-${o}`}
                              onClick={() => setAnswers({ ...answers, [q.id]: on ? list.filter((x) => x !== o) : [...list, o] })}
                              className={`min-h-[44px] rounded-full px-4 text-sm font-medium border transition-colors ${
                                on ? "bg-[#004aad] text-white border-[#004aad]"
                                   : "bg-white text-slate-700 border-slate-200 hover:border-[#004aad]"}`}>
                        {o}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}


        </div>

        <button onClick={submit} disabled={sending} data-testid="booking-submit-btn"
                className="w-full mt-7 inline-flex items-center justify-center gap-2 min-h-[52px] rounded-full bg-[#ff731d] px-8 text-base font-semibold text-white hover:bg-[#e6651a] disabled:opacity-60 transition-colors">
          {sending && <Loader2 className="h-4 w-4 animate-spin" />} {c.confirm}
        </button>
        <p className="text-xs text-slate-400 mt-4 text-center">
          {c.smallPrint}
        </p>
      </div>
    );
  }

  // ---------- step 2: hours of the chosen day ----------
  if (pickedDay) {
    const d = new Date(`${pickedDay}T12:00:00Z`);
    return shell(
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-5 sm:p-8">
        <button onClick={() => setPickedDay(null)} data-testid="booking-back-to-calendar"
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004aad] mb-5">
          <ArrowLeft className="h-4 w-4" /> {c.otherDay}
        </button>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#004aad]">
          {lang === "en"
            ? `${WEEK[(d.getUTCDay() + 6) % 7]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
            : `${WEEK[(d.getUTCDay() + 6) % 7]} ${d.getUTCDate()} de ${MONTHS[d.getUTCMonth()]}`}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {selectedType?.minutes} {c.hoursIn} ({tz.replace("_", " ")})
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-6" data-testid="booking-slots">
          {dayHours.map((s) => (
            <button key={s} onClick={() => setChosen(s)} data-testid={`booking-slot-${s}`}
                    className="min-h-[52px] rounded-xl border border-[#004aad]/25 bg-white text-base font-semibold text-[#004aad] hover:bg-[#004aad] hover:text-white transition-colors">
              {hour(s)}
            </button>
          ))}
        </div>
        {tzBar}
      </div>
    );
  }

  // ---------- step 1: month calendar ----------
  return shell(
    <>
      {!!page.title && (
        <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-[#004aad]"
            data-testid="booking-page-title">
          {page.title}
        </h1>
      )}
      {page.intro && <p className="text-base text-slate-600 mt-2.5 leading-relaxed">{page.intro}</p>}
      {page.consumes_sessions && (
        <p className="mt-4 rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-slate-700"
           data-testid="booking-cycle-note">
          {c.cycleNote}
        </p>
      )}

      <div className="mt-6 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-4 sm:p-7">
        {(page.meeting_types || []).length > 1 && (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="booking-type-picker">
            {page.meeting_types.map((mt) => (
              <button key={mt.id} onClick={() => setTypeId(mt.id)} data-testid={`booking-type-${mt.id}`}
                      className={`min-h-[44px] rounded-full px-4 text-sm font-semibold transition-colors ${
                        mt.id === typeId ? "bg-[#004aad] text-white shadow-md"
                                         : "bg-white border border-slate-200 text-slate-700 hover:border-[#004aad]"}`}>
                {mt.name} · {mt.minutes} min
              </button>
            ))}
          </div>
        )}
        <div className={`flex flex-wrap gap-2 mb-5 ${(page.meeting_types || []).length > 1 ? "hidden" : "hidden"}`}
             data-testid="booking-type-picker-hidden" />

        <div className="flex items-center justify-between">
          <button onClick={() => setMonth(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))}
                  data-testid="booking-prev-month"
                  className="h-11 w-11 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="text-base md:text-lg font-semibold text-slate-900">
            {MONTHS[month.m]} {month.y}
          </p>
          <button onClick={() => setMonth(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))}
                  data-testid="booking-next-month"
                  className="h-11 w-11 grid place-items-center rounded-full hover:bg-slate-100 text-slate-500">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mt-4 text-center text-xs uppercase tracking-wider text-slate-400">
          {WEEK.map((w) => <div key={w} className="py-1">{w}</div>)}
        </div>

        {loadingSlots ? (
          <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#004aad]" /></div>
        ) : (
          <div className="grid grid-cols-7 gap-1 mt-1" data-testid="booking-calendar">
            {grid.map((c, i) => c === null ? <div key={`x${i}`} /> : (
              <button key={c.key} disabled={!c.count} onClick={() => setPickedDay(c.key)}
                      data-testid={`booking-day-${c.key}`}
                      className={`relative h-12 rounded-xl text-base transition-colors ${
                        c.count ? "font-bold text-[#004aad] border border-[#004aad]/30 hover:bg-[#004aad] hover:text-white"
                                : "text-slate-300"}`}>
                {c.day}
                {!!c.count && <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-[#ff731d]" />}
              </button>
            ))}
          </div>
        )}

        {!loadingSlots && Object.keys(localDays).length === 0 && (
          <p className="text-sm text-slate-500 text-center mt-5" data-testid="booking-no-slots">
            {c.noSlots}{" "}
            <a className="text-[#004aad] hover:underline" href="mailto:admin@wespeakspanish-cr.com">admin@wespeakspanish-cr.com</a>.
          </p>
        )}
        {tzBar}
      </div>
    </>
  );
}
